import React, { useState, useEffect, useMemo } from "react";
import {
  DataGridPro,
  GridValueGetterParams,
  useGridApiRef,
  GridRenderCellParams,
  GridColDef,
} from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { useTranslation } from "react-i18next";
import { IMessageBoxParams } from "../../../common/MessageBox";
import { IConfirmDialogParams } from "../../../common/ConfirmDialog";
import { getProjects } from "../../../webapi/service/getProjects";
import { ISelectProjectParam } from "../../../webapi/constants";
import {
  createProject,
  ICreateProjectRequesuModel,
} from "../../../webapi/service/createProject";
import {
  updateProject,
  IUpdateProjectsRequestModel,
} from "../../../webapi/service/updateProject";
import { deleteProjects } from "../../../webapi/service/deleteProjects";
import { copyProject } from "../../../webapi/service/copyProject";
import AuthService from "../../../common/AuthService";
import { AppConfig, ProjectStatus, AnyType } from "../../../common/constants";
import ReloadButton from "../../common/ReloadButton";
import AddProjectButton from "./components/AddProjectButton";
import DeleteButton from "../common/DeleteButton";
import EditButton from "../../common/EditButton";
import CopyButton from "./components/CopyButton";
import SaveButton from "../../common/SaveButton";
import CancelButton from "../../common/CancelButton";
import { IProjectRow } from "../../../webapi/service/getProjects";
import PromptDialog, { InputTextItem } from "../../../common/PromptDialog";
import "../../main.css";
import "./projectlist.css";

export interface IProps {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (params: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   * @param onCloseMessage - メッセージBoxを閉じる場合に呼び出します
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined,
    onCloseMessage?: () => void
  ) => void;
  /**
   * プロジェクトを選択した場合に呼び出します
   */
  onSelect: (projectid: string) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}

export default function ProjectListMainSpace(props: IProps): JSX.Element {
  const StatusNone = ProjectStatus.NONE;
  const StatusDoing = ProjectStatus.DOING;
  const StatusDone = ProjectStatus.DONE;
  const { i18n, t } = useTranslation();
  const apiRef = useGridApiRef();
  const [rows, setRows] = useState<IProjectRow[]>([]);
  const [buttonVisible, setButtonVisible] = useState<boolean>(false);
  const [createButtonEnabled, setCreateButtonEnabled] = useState(true);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);

  useEffect(() => {
    loadProjects(true);
  }, []);

  function clearEditMode() {
    const { getRowModels, getRowMode, setRowMode } = apiRef.current;
    getRowModels().forEach((row) => {
      if (getRowMode(row.id) === "edit") {
        setRowMode(row.id, "view");
      }
    });
  }

  function clearCheckRows() {
    apiRef.current.selectRows(apiRef.current.getAllRowIds(), false);
    setButtonVisible(false);
  }

  function loadProjects(loading: boolean) {
    const { authService, onLoading, onCommunicationError } = props;
    onLoading(loading);
    clearEditMode();
    clearCheckRows();
    setCreateButtonEnabled(true);
    getProjects({
      authService: authService,
      success: (rows) => {
        setRows(rows);
        setTimeout(() => {
          onLoading(false);
        });
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  function handleReload() {
    props.resetUnoperatedTimeout();
    loadProjects(true);
  }

  function blurGridRow() {
    if (apiRef.current) {
      const actions = apiRef.current.getColumnHeaderElement("actions");
      if (actions) {
        actions.focus();
      }
    }
  }

  function afterRegist() {
    blurGridRow();
    loadProjects(true);
  }

  function createProject_Internal(param: ICreateProjectRequesuModel) {
    const {
      authService,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = props;
    onLoading(true);
    resetUnoperatedTimeout();
    createProject({
      authService: authService,
      name: param.name,
      memo: param.memo,
      success: () => {
        afterRegist();
      },
      fail: (id, status) => {
        onCommunicationError(id, status, afterRegist);
      },
    });
  }

  function updateProject_Internal(
    project: ISelectProjectParam,
    param: IUpdateProjectsRequestModel
  ) {
    const {
      authService,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = props;
    const { projectid, etag } = project;
    onLoading(true);
    resetUnoperatedTimeout();
    updateProject({
      authService: authService,
      projectid: projectid,
      name: param.name,
      memo: param.memo,
      etag: etag,
      success: () => {
        afterRegist();
      },
      fail: (id, status) => {
        onCommunicationError(id, status, afterRegist);
      },
    });
  }

  function deleteProject_Internal(selectProjects: ISelectProjectParam[]) {
    const {
      authService,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = props;
    onLoading(true);
    resetUnoperatedTimeout();
    deleteProjects({
      authService: authService,
      projects: selectProjects,
      success: () => {
        loadProjects(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  function copyProject_Internal(selectProject: ISelectProjectParam) {
    const {
      authService,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = props;
    const { projectid, etag } = selectProject;
    onLoading(true);
    resetUnoperatedTimeout();
    copyProject({
      authService: authService,
      projectid: projectid,
      etag: etag,
      success: () => {
        loadProjects(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  function handleOpenNewDialog() {
    setShowNewProjectDialog(true);
  }

  function handleRegistNewProject(texts: InputTextItem[]) {
    const name = texts[0].text;
    const memo = texts[1].text;
    createProject_Internal({
      name: name,
      memo: memo,
    });
    setShowNewProjectDialog(false);
  }

  function handleCloseNewDialog() {
    setShowNewProjectDialog(false);
  }

  function handleOpenCopyDialog(row: IProjectRow | undefined) {
    if (!row) {
      return;
    }
    const { showConfirm } = props;
    const { projectid, etag } = row;
    showConfirm({
      title: t("project_copy_confirm"),
      message: t("project_copy_confirm_message"),
      onOk: () => copyProject_Internal({ projectid: projectid, etag: etag }),
    });
  }

  function handleOpenDeleteDialog() {
    const { showConfirm } = props;
    const projects: ISelectProjectParam[] = [];
    const map = apiRef.current.getSelectedRows();
    map.forEach((value) => {
      projects.push({ projectid: value.projectid, etag: value.etag });
    });
    showConfirm({
      title: t("project_delete_confirm"),
      message: t("project_delete_confirm_message"),
      onOk: () => deleteProject_Internal(projects),
    });
  }

  function handleClickEdit(params: GridRenderCellParams<AnyType, IProjectRow>) {
    apiRef.current.setRowMode(params.id, "edit");
  }

  function handleClickSave(params: GridRenderCellParams<AnyType, IProjectRow>) {
    const { id } = params;
    apiRef.current.commitRowChange(id);
  }

  function handleClickCancel(
    params: GridRenderCellParams<AnyType, IProjectRow>
  ) {
    const { id, row } = params;
    const { projectid } = row;
    if (projectid.length === 0) {
      loadProjects(true);
    } else {
      apiRef.current.setRowMode(id, "view");
    }
  }

  function convertStatus(status: string) {
    switch (status) {
      case StatusNone:
        return t("project_list_none");
      case StatusDoing:
        return t("project_list_doing");
      case StatusDone:
        return t("project_list_done");
    }
    return status;
  }

  interface GridCellExpandProps {
    value: string;
    width: number;
  }

  function isOverflown(element: Element): boolean {
    return (
      element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth
    );
  }

  const GridCellExpand = React.memo(function GridCellExpand(
    props: GridCellExpandProps
  ) {
    const { width, value } = props;
    const wrapper = React.useRef<HTMLDivElement | null>(null);
    const cellDiv = React.useRef(null);
    const cellValue = React.useRef(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [showFullCell, setShowFullCell] = React.useState(false);
    const [showPopper, setShowPopper] = React.useState(false);

    const handleMouseEnter = () => {
      const isCurrentlyOverflown = cellValue.current
        ? isOverflown(cellValue.current)
        : false;
      setShowPopper(isCurrentlyOverflown);
      setAnchorEl(cellDiv.current);
      setShowFullCell(true);
    };

    const handleMouseLeave = () => {
      setShowFullCell(false);
    };

    return (
      <Box
        ref={wrapper}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          alignItems: "center",
          lineHeight: "24px",
          width: 1,
          height: 1,
          position: "relative",
          display: "flex",
        }}
      >
        <Box
          ref={cellDiv}
          sx={{
            height: 1,
            width: width,
            display: "block",
            position: "absolute",
            top: 0,
          }}
        />
        <Box
          ref={cellValue}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Box>
        {showPopper && (
          <Popper
            open={showFullCell && Boolean(anchorEl)}
            anchorEl={anchorEl}
            placement="bottom-start"
            className="projectlist-popper"
            style={{ minWidth: width }}
          >
            <Paper elevation={1} className="projectlist-popper-item">
              {value}
            </Paper>
          </Popper>
        )}
      </Box>
    );
  });

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "actions",
        type: "actions",
        width: 100,
        renderCell: (params: GridRenderCellParams<AnyType, IProjectRow>) => {
          const { id, row } = params;
          const api = apiRef.current;
          const isEdit = api.getRowMode(id) === "edit";
          const rowCount = api.getRowsCount();
          const copyBtn =
            rowCount < AppConfig.MAX_PROJECTS ? (
              <CopyButton onClick={() => handleOpenCopyDialog(row)} />
            ) : (
              <div style={{ width: "40px", height: "40px" }}></div>
            );
          const contents = !isEdit ? (
            <div style={{ display: "flex" }}>
              <EditButton onClick={() => handleClickEdit(params)} />
              {copyBtn}
            </div>
          ) : (
            <div>
              <SaveButton onClick={() => handleClickSave(params)} />
              <CancelButton onClick={() => handleClickCancel(params)} />
            </div>
          );
          return contents;
        },
      },
      {
        field: "projectid",
        headerName: t("project_list_id"),
        width: 200,
        hide: true,
      },
      {
        field: "name",
        headerName: t("project_list_name"),
        width: 180,
        sortable: true,
        editable: true,
      },
      {
        field: "updatetime",
        headerName: t("project_list_lastupdate"),
        width: 160,
        sortable: true,
      },
      {
        field: "memo",
        headerName: t("project_list_memo"),
        width: 200,
        sortable: true,
        editable: true,
        renderCell: (params: GridRenderCellParams<string>) => {
          return (
            <GridCellExpand
              value={params.value || ""}
              width={params.colDef.computedWidth}
            />
          );
        },
      },
      {
        field: "filenum",
        headerName: t("project_list_number_files"),
        width: 110,
        sortable: true,
      },
      {
        field: "mcreatests",
        headerName: t("project_list_model"),
        width: 250,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const { mcreatests, mcreatetime } = params.row;
          const cratetime =
            mcreatests === StatusDone ? "(" + mcreatetime + ")" : "";
          return convertStatus(mcreatests) + cratetime;
        },
      },
      {
        field: "pcreatests",
        headerName: t("project_list_profile"),
        width: 250,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const { pcreatests, pcreatetime } = params.row;
          const cratetime =
            pcreatests === StatusDone ? "(" + pcreatetime + ")" : "";
          return convertStatus(pcreatests) + cratetime;
        },
      },
      {
        field: "etag",
        headerName: t("project_list_etag"),
        width: 200,
        hide: true,
      },
    ];
  }, [i18n.language]);

  const { onSelect } = props;
  const isCreateEnabled = rows.length < AppConfig.MAX_PROJECTS;
  const createBtn =
    isCreateEnabled && createButtonEnabled ? (
      <AddProjectButton onClick={handleOpenNewDialog} />
    ) : (
      ""
    );
  const deleteBtn = buttonVisible ? (
    <DeleteButton onClick={handleOpenDeleteDialog} />
  ) : (
    ""
  );
  const dataGrid = useMemo(() => {
    return (
      <DataGridPro
        columns={columns}
        rows={rows}
        checkboxSelection
        disableSelectionOnClick
        disableColumnMenu
        editMode="row"
        onSelectionModelChange={(selecttionModel) => {
          setButtonVisible(selecttionModel.length > 0 ? true : false);
        }}
        onRowClick={(params) => {
          const api = apiRef.current;
          if (api && api.getRowMode(params.id) === "edit") {
            return;
          }
          onSelect(params.row.projectid);
        }}
        onCellFocusOut={(params, event) => {
          event.defaultMuiPrevented = true;
        }}
        getRowClassName={() => {
          return "grid-row";
        }}
        apiRef={apiRef}
        onRowEditCommit={(id, event) => {
          const { getEditRowsModel, getRow } = apiRef.current;
          const { name, memo } = getEditRowsModel()[id];
          const nameValue = name.value ? name.value.toString() : "";
          const memoValue = memo.value ? memo.value.toString() : "";
          const row = getRow(id);
          if (row) {
            const { projectid, etag } = row;
            updateProject_Internal(
              { projectid: projectid, etag: etag },
              { name: nameValue, memo: memoValue }
            );
          } else {
            event.defaultMuiPrevented = true;
          }
        }}
        localeText={{
          noRowsLabel: "",
          footerTotalRows: t("project_list_total_rows") + " : ",
        }}
        isRowSelectable={(params) => {
          return params.row.projectid.length > 0;
        }}
      />
    );
  }, [rows, i18n.language]);

  return (
    <React.Fragment>
      <div className="mainspace">
        <div className="mainspace-toolbar">
          <ReloadButton onClick={handleReload} />
          {createBtn}
          {deleteBtn}
        </div>
        <div className="projectlist-grid">{dataGrid}</div>
      </div>
      <PromptDialog
        show={showNewProjectDialog}
        title={t("project_register")}
        items={[
          {
            id: "name",
            label: t("project_register_name"),
            maxLength: 120,
          },
          {
            id: "memo",
            label: t("project_register_memo"),
            maxLength: 120,
          },
        ]}
        onOk={handleRegistNewProject}
        onCancel={handleCloseNewDialog}
      />
    </React.Fragment>
  );
}
